import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { CopyableId } from '@/components/common';
import { winApi, painPointApi } from '@/services/skillKnowledgeApi';
import type { Win, PainPoint } from '@/types/skillKnowledge';
import type { ProfilePainPoint } from '@/types/userProfile';
import type { LearningPlan } from '@/types/learningManagement';
import { Trophy, AlertCircle, BookOpen } from 'lucide-react';

type WinEntry = string | { winId?: string; win_description?: string; winDescription?: string };

export interface WinsAndPainPointsCardProps {
  winsLookingFor?: WinEntry[];
  painPoints?: ProfilePainPoint[];
  /** When provided, shows win and pain points from the user's learning path (from learning path API). */
  learningPlan?: LearningPlan | null;
}

/** Normalize API shape: string or { winId, winDescription } -> { id, display } */
function normalizeWinsList(raw: WinEntry[] | undefined): { id: string; display: string }[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => {
    if (typeof item === 'string') return { id: item, display: item };
    const obj = item as Record<string, unknown>;
    const id = String(obj.winId ?? obj.win_id ?? '');
    const desc = String(obj.winDescription ?? obj.win_description ?? obj.description ?? id);
    return { id: id || desc, display: desc || id };
  }).filter((x) => x.display.length > 0 || x.id.length > 0);
}

/** Pain point from learning plan can be full object or { painPointId, painPointDescription } */
function getPlanPainPointDisplay(pp: Record<string, unknown>, masterById: Map<string, PainPoint>): { name: string; description?: string } {
  const id = String(pp.painPointId ?? pp.pain_point_id ?? '').trim();
  const desc = String(pp.painPointDescription ?? pp.pain_point_description ?? pp.description ?? '').trim();
  const master = id ? masterById.get(id) : undefined;
  return {
    name: master?.name ?? master?.description ?? (desc || id || '—'),
    description: master?.description || desc || undefined,
  };
}

export const WinsAndPainPointsCard: React.FC<WinsAndPainPointsCardProps> = ({
  winsLookingFor,
  painPoints,
  learningPlan,
}) => {
  const winsList = normalizeWinsList(winsLookingFor);
  const painList = painPoints ?? [];

  const [wins, setWins] = useState<Win[]>([]);
  const [painPointsMaster, setPainPointsMaster] = useState<PainPoint[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [winsRes, ppRes] = await Promise.all([
          winApi.list({ page: 0, size: 500 }),
          painPointApi.list({ page: 0, size: 500 }),
        ]);
        setWins(winsRes.content ?? []);
        setPainPointsMaster(ppRes.content ?? []);
      } catch (e) {
        console.error('[WinsAndPainPointsCard] Failed to load wins/pain points:', e);
      }
    };
    load();
  }, []);

  const winById = new Map<string, Win>();
  wins.forEach((w) => { if (w.id) winById.set(w.id, w); });
  const painPointById = new Map<string, PainPoint>();
  painPointsMaster.forEach((pp) => { if (pp.id) painPointById.set(pp.id, pp); });

  const getWinDisplay = (entry: { id: string; display: string }) =>
    winById.get(entry.id)?.name ?? entry.display;

  const getWinDescription = (entry: { id: string; display: string }) =>
    winById.get(entry.id)?.description ?? undefined;

  const getPainPointDisplay = (pp: ProfilePainPoint) => {
    const master = painPointById.get(pp.painPointId);
    return master?.name ?? master?.description ?? pp.description ?? pp.painPointId;
  };

  const getPainPointDescription = (pp: ProfilePainPoint) =>
    painPointById.get(pp.painPointId)?.description ?? pp.description ?? undefined;

  const planWin = learningPlan?.win;
  const planWinDisplay = planWin
    ? (planWin.winId && winById.get(planWin.winId)?.name) ?? planWin.winDescription ?? (planWin.winId && winById.get(planWin.winId)?.description) ?? planWin.winId ?? '—'
    : null;
  const planWinDescription = planWin && planWin.winId ? winById.get(planWin.winId)?.description : planWin?.winDescription ?? undefined;
  const planPainPoints = Array.isArray(learningPlan?.painPoints) ? learningPlan.painPoints : [];

  const hasWins = winsList.length > 0;
  const hasPainPoints = painList.length > 0;
  const hasPlanWin = !!planWinDisplay;
  const hasPlanPainPoints = planPainPoints.length > 0;
  const hasAny = hasWins || hasPainPoints || hasPlanWin || hasPlanPainPoints;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-amber-500" />
          Wins & Pain Points
        </CardTitle>
        <CardDescription>
          Wins and pain points from profile and learning path (with details from master data)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {!hasAny ? (
          <div className="rounded-lg border border-dashed bg-muted/20 flex flex-col items-center justify-center py-12 text-center">
            <Trophy className="h-10 w-10 text-muted-foreground/50 mb-3" />
            <p className="font-medium text-muted-foreground">No wins or pain points yet</p>
            <p className="text-sm text-muted-foreground mt-1">Add these in the Bio & Summary tab or generate a learning path</p>
          </div>
        ) : (
          <>
            {hasWins && (
              <div className="rounded-lg border bg-muted/20 p-4 space-y-3">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <Trophy className="h-4 w-4" />
                  Wins looking for (profile)
                </h3>
                <div className="space-y-3">
                  {winsList.map((entry, idx) => {
                    const name = getWinDisplay(entry);
                    const description = getWinDescription(entry);
                    return (
                      <div key={`win-${entry.id}-${idx}`} className="rounded-lg border bg-background/60 dark:bg-background/40 p-3 space-y-1.5">
                        <p className="font-medium">{name}</p>
                        {description && <p className="text-sm text-muted-foreground">{description}</p>}
                        {entry.id && winById.has(entry.id) && (
                          <CopyableId id={entry.id} label="Win ID" showLabel={true} codeClassName="text-xs" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {hasPainPoints && (
              <div className="rounded-lg border bg-muted/20 p-4 space-y-3">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <AlertCircle className="h-4 w-4" />
                  Pain points (profile)
                </h3>
                <div className="space-y-3">
                  {painList.map((pp, idx) => {
                    const name = getPainPointDisplay(pp);
                    const description = getPainPointDescription(pp);
                    return (
                      <div key={`pp-${pp.painPointId}-${idx}`} className="rounded-lg border bg-background/60 dark:bg-background/40 p-3 space-y-1.5">
                        <p className="font-medium">{name}</p>
                        {description && <p className="text-sm text-muted-foreground">{description}</p>}
                        <CopyableId id={pp.painPointId} label="Pain point ID" showLabel={true} codeClassName="text-xs" />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {(hasPlanWin || hasPlanPainPoints) && (
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                  <BookOpen className="h-4 w-4" />
                  From learning path
                </h3>
                <p className="text-xs text-muted-foreground">Retrieved from the user&apos;s learning path API</p>
                {hasPlanWin && (
                  <div className="rounded-lg border bg-background/60 dark:bg-background/40 p-3 space-y-1.5">
                    <p className="font-medium">{planWinDisplay}</p>
                    {planWinDescription && <p className="text-sm text-muted-foreground">{planWinDescription}</p>}
                    {planWin?.winId && <CopyableId id={planWin.winId} label="Win ID" showLabel={true} codeClassName="text-xs" />}
                  </div>
                )}
                {hasPlanPainPoints && (
                  <div className="space-y-2">
                    {planPainPoints.map((pp, idx) => {
                      const raw = pp as unknown as Record<string, unknown>;
                      const { name, description } = getPlanPainPointDisplay(raw, painPointById);
                      const id = String(raw.painPointId ?? raw.pain_point_id ?? '').trim();
                      return (
                        <div key={`plan-pp-${id}-${idx}`} className="rounded-lg border bg-background/60 dark:bg-background/40 p-3 space-y-1.5">
                          <p className="font-medium">{name}</p>
                          {description && <p className="text-sm text-muted-foreground">{description}</p>}
                          {id && <CopyableId id={id} label="Pain point ID" showLabel={true} codeClassName="text-xs" />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
